import { CheckIcon } from "lucide-react";
import { useCallback } from "react";
import { clsx } from "clsx";
import { useDropdownContext } from "../Dropdown";
import { useSelectContext } from "./context";
import type { ISelectOption as SelectOptionType } from "./types";
import { TruncatedContent } from "@/ui-kit";

type SelectItemProps = {
  option: SelectOptionType;
  index: number;
};

export const SelectItem = ({ option, index }: SelectItemProps) => {
  const { value, multiple, onRenderOption, onSelect, contentId } =
    useSelectContext();
  const { navigationRef, getItemProps, activeIndex } = useDropdownContext();

  const optionId = `${contentId}-option-${index}`;

  const isSelected = multiple
    ? Array.isArray(value) && value.includes(option.id)
    : value === option.id;

  const isActive = activeIndex === index;

  const handleClick = useCallback(() => {
    if (option.disabled) {
      return;
    }
    onSelect(option.id);
  }, [onSelect, option.id, option.disabled]);

  return (
    <button
      {...(option.dataAttributes || {})}
      id={optionId}
      role="option"
      aria-selected={isSelected}
      aria-disabled={option.disabled}
      className={clsx(
        "nuco-select__option",
        option.disabled && "nuco-select__option--disabled",
        isActive && "nuco-select__option--active",
        isSelected && "nuco-select__option--selected",
        option.className,
      )}
      disabled={option.disabled}
      ref={navigationRef(index)}
      {...getItemProps?.()}
      onClick={handleClick}
      tabIndex={isActive ? 0 : -1}
    >
      {onRenderOption ? (
        onRenderOption(option)
      ) : (
        <>
          {option.icon && (
            <div className="nuco-select__option-icon">{option.icon}</div>
          )}
          <TruncatedContent
            className="nuco-typography--body nuco-select__option-text"
            content={option.text}
          />
          {option.description && (
            <TruncatedContent
              className="nuco-select__option-description"
              content={option.description}
            />
          )}
        </>
      )}

      <span
        aria-hidden
        className={clsx(
          "nuco-select__option-checkMark",
          isSelected ? "nuco-select__option-checkMark--visible" : "",
        )}
      >
        <CheckIcon size="1rem" />
      </span>
    </button>
  );
};
